import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import heroBoardroom from '../../assets/hero-boardroom.png';
import heroBuilding from '../../assets/hero-building.png';
import heroOffice from '../../assets/hero-office.png';

const HeroCarousel = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      id: 1,
      image: heroBoardroom,
      titleAr: 'خدمات قانونية تغطي احتياجاتك كاملاً',
      titleEn: 'Comprehensive Legal Services for Every Need',
      descriptionAr:
        'من صياغة العقود والتوثيق إلى التمثيل القضائي والاستشارات المتخصصة، تقدم وازن حلولاً قانونية شاملة بمعايير احترافية عالية.',
      descriptionEn:
        'From contract drafting and notarization to litigation and specialized consultations, Wazin delivers comprehensive legal solutions at the highest professional standards.',
      ctaTextAr: 'استكشف الخدمات',
      ctaTextEn: 'Explore Services',
      ctaLink: '/services',
      imagePosition: 'object-center'
    },
    {
      id: 2,
      image: heroBuilding,
      titleAr: 'احجز استشارتك القانونية الآن',
      titleEn: 'Book Your Legal Consultation Now',
      descriptionAr:
        'احصل على استشارة قانونية متخصصة من فريق وازن، بتوجيه واضح وتحليل دقيق يساعدك على اتخاذ القرار الصحيح.',
      descriptionEn:
        'Get a specialized legal consultation from the Wazin team, with clear guidance and precise analysis to help you make the right decision.',
      ctaTextAr: 'احجز استشارة',
      ctaTextEn: 'Book Consultation',
      ctaLink: '/book-consultation',
      imagePosition: 'object-center'
    },
    {
      id: 3,
      image: heroOffice,
      titleAr: 'خدمات قانونية احترافية في مكان واحد',
      titleEn: 'Professional Legal Services in One Place',
      descriptionAr:
        'من الاستشارات القانونية وصياغة العقود إلى التمثيل القضائي والخدمات التخصصية، تقدم وازن حلولاً عملية بمعايير مهنية عالية.',
      descriptionEn:
        'From legal consultations and contract drafting to litigation support and specialized services, Wazin delivers practical legal solutions with high professional standards.',
      ctaTextAr: 'استكشف الخدمات',
      ctaTextEn: 'Explore Services',
      ctaLink: '/services',
      imagePosition: 'object-center'
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [slides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  return (
    <div className="relative w-full h-[380px] md:h-[550px] overflow-hidden bg-gray-900">
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-all duration-700 ease-in-out ${
            index === currentSlide ? 'opacity-100 z-10 scale-100' : 'opacity-0 z-0 scale-105'
          }`}
          style={{ transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)' }}
        >
          <div className="absolute inset-0 overflow-hidden">
            <img
              src={slide.image}
              alt={i18n.language === 'ar' ? slide.titleAr : slide.titleEn}
              className={`w-full h-full object-cover ${slide.imagePosition} scale-[1.03] blur-[2px] brightness-[0.78] saturate-[0.88]`}
            />
            <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(18,39,69,0.80)_0%,rgba(18,39,69,0.62)_44%,rgba(8,12,20,0.52)_100%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(200,164,90,0.12),transparent_58%)]" />
          </div>

          <div className="relative z-10 h-full flex items-center">
            <div className="container mx-auto px-4 sm:px-6 lg:px-12">
              <div className="max-w-3xl mx-auto text-center" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
                <h1
                  className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight animate-fadeIn"
                  style={{
                    animationDelay: '0.2s',
                    textShadow: '0 4px 12px rgba(0,0,0,0.75), 0 2px 6px rgba(0,0,0,0.55)'
                  }}
                >
                  {i18n.language === 'ar' ? slide.titleAr : slide.titleEn}
                </h1>

                <p
                  className="text-base md:text-lg lg:text-xl text-white/95 mb-8 animate-fadeIn leading-relaxed max-w-2xl mx-auto"
                  style={{
                    animationDelay: '0.4s',
                    textShadow: '0 2px 8px rgba(0,0,0,0.65)'
                  }}
                >
                  {i18n.language === 'ar' ? slide.descriptionAr : slide.descriptionEn}
                </p>

                <button
                  onClick={() => navigate(slide.ctaLink)}
                  className="bg-[#A27849] text-white px-8 py-3 rounded-lg text-base md:text-lg font-bold hover:bg-[#A27849]/90 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl animate-fadeIn"
                  style={{ animationDelay: '0.6s' }}
                >
                  {i18n.language === 'ar' ? slide.ctaTextAr : slide.ctaTextEn}
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}

      <button
        onClick={prevSlide}
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 bg-white/10 backdrop-blur-md border-2 border-wazin-gold hover:bg-wazin-gold/80 text-wazin-gold hover:text-wazin-navy p-3 md:p-4 rounded-full transition-all duration-300 hover:scale-110 shadow-lg group"
        aria-label="Previous Slide"
      >
        <svg className="w-6 h-6 md:w-8 md:h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      <button
        onClick={nextSlide}
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 bg-white/10 backdrop-blur-md border-2 border-wazin-gold hover:bg-wazin-gold/80 text-wazin-gold hover:text-wazin-navy p-3 md:p-4 rounded-full transition-all duration-300 hover:scale-110 shadow-lg group"
        aria-label="Next Slide"
      >
        <svg className="w-6 h-6 md:w-8 md:h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </button>

      <div className="absolute bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`rounded-full transition-all duration-300 ease-out ${
              index === currentSlide
                ? 'bg-[#A27849] w-16 md:w-20 h-3 shadow-lg border-2 border-[#A27849]'
                : 'bg-white/60 hover:bg-[#A27849]/50 w-3 h-3 border border-white/60'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.8s ease-out forwards;
          opacity: 0;
        }
      `}</style>
    </div>
  );
};

export default HeroCarousel;
